import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditRoleComposer } from "@/forms/rema/editRole/composer"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ShieldCheck, Loader2 } from "lucide-react"
import { useToast } from "@/contexts/ToastContext"

function EditRolePage() {
  const { roleNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Intentionally fetches the full list then finds client-side. reMa has no
  // getRoleByNumber endpoint, and getRoles returns the entire table (no
  // server-side pagination), so the record is always present client-side and the
  // list is normally already in cache from the list view. This is NOT the
  // pagination bug fixed on the other domains — switch to a single-record fetch
  // only once the backend adds getRoleByNumber.
  const { data, isLoading } = useApiQuery("getRoles", {})
  const role = ((data as any)?.content?.roles || (data as any)?.roles || []).find(
    (r: any) => String(r.roleNumber) === String(roleNumber)
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Role</CardTitle>
              <CardDescription className="text-base">Update role configuration</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <EditRoleComposer
            showHeader={false}
            initialValues={{
              roleNumber: role?.roleNumber,
              roleType: role?.roleType as any,
              namespace: role?.namespace as any,
              organization: role?.organization ?? '',
              roleName: role?.roleName ?? '',
              roleDescription: role?.roleDescription ?? '',
            }}
            submitLabel="Save Changes"
            onSuccess={() => navigate({ to: '/rema/roles' })}
            onError={(error) => toast({ title: 'Error', description: `Failed to update role: ${error.message}`, type: 'error' })}
            onCancel={() => navigate({ to: '/rema/roles' })}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/rema/roles/$roleNumber_/edit")({
  staticData: {
    title: 'Edit Role',
    isSecured: true,
  },
  component: EditRolePage,
})
